import React from "react";
import * as dmsService from "../../../services/dmsService";
import { fmtMoney } from "../../Fin/gl/glContext";
import { hint, linkBtn } from "../../Fin/gl/glUi";
import { FormDrawer, SecondaryButton, inputStyle, useLoad } from "../../../components/ui";

// Warranty claims raised by hand: a claim on an invoiced repair order for work that was missed on the
// first claim (supplementary) or that the manufacturer agreed to pay as a favour (goodwill), and the
// lines of any draft claim, which can be corrected before it is submitted.

export const CLAIM_TYPES = { REPAIR: "From the repair order", SUPPLEMENTARY: "Supplementary", GOODWILL: "Goodwill" };
const KINDS = [
  { label: "Labour", value: "LABOUR" },
  { label: "Part", value: "PART" },
  { label: "Sublet", value: "SUBLET" },
];
const blankLine = () => ({ line_kind: "LABOUR", description: "", quantity: 1, amount: "" });
const warnBox = { background: "#FFFBEB", border: "1px solid #FCD34D", color: "#92400E", borderRadius: 8, padding: "10px 12px", fontSize: 13, marginBottom: 16 };

// Rows as typed, ready for the API: empty rows are dropped.
export const claimLinesPayload = (lines) =>
  (lines || [])
    .filter((l) => String(l.description || "").trim() || String(l.amount ?? "") !== "")
    .map((l) => ({ line_kind: l.line_kind, description: l.description, quantity: l.quantity === "" ? 1 : Number(l.quantity), amount: Number(l.amount), ...(l.job_id ? { job_id: l.job_id } : {}) }));

const total = (lines) => (lines || []).reduce((s, l) => s + (Number(l.amount) || 0), 0);

export function ClaimLinesEditor({ lines, onChange }) {
  const set = (i, patch) => onChange(lines.map((l, n) => (n === i ? { ...l, ...patch } : l)));
  return (
    <div className="mb-3">
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>What is claimed</div>
      <div style={hint}>One line per piece of work. The amount is the total for that line, before VAT (claims carry no VAT).</div>
      <div style={{ overflowX: "auto" }}>
        <table className="table table-sm mb-1" style={{ minWidth: 560 }}>
          <thead>
            <tr style={{ fontSize: 12, color: "#6B7280" }}>
              <th style={{ width: 110 }}>Kind</th>
              <th>Description</th>
              <th style={{ width: 80 }}>Qty</th>
              <th style={{ width: 120 }}>Amount</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>
                  <select className="form-select form-select-sm" aria-label={`Line ${i + 1} kind`} value={l.line_kind} onChange={(e) => set(i, { line_kind: e.target.value })}>
                    {KINDS.map((k) => (
                      <option key={k.value} value={k.value}>
                        {k.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input className="form-control form-control-sm" style={inputStyle} aria-label={`Line ${i + 1} description`} maxLength={240} value={l.description} onChange={(e) => set(i, { description: e.target.value })} />
                </td>
                <td>
                  <input className="form-control form-control-sm" style={inputStyle} type="number" min="0" step="any" aria-label={`Line ${i + 1} quantity`} value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
                </td>
                <td>
                  <input className="form-control form-control-sm" style={inputStyle} type="number" min="0" step="any" aria-label={`Line ${i + 1} amount`} value={l.amount} onChange={(e) => set(i, { amount: e.target.value })} />
                </td>
                <td>
                  <button type="button" style={linkBtn} aria-label={`Remove line ${i + 1}`} disabled={lines.length === 1} onClick={() => onChange(lines.filter((_, n) => n !== i))}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-between align-items-center">
        <button type="button" style={linkBtn} onClick={() => onChange([...lines, blankLine()])}>
          + Add line
        </button>
        <div style={{ fontSize: 14 }}>
          Total <b>{fmtMoney(total(lines))}</b>
        </div>
      </div>
    </div>
  );
}

// What the chosen repair order already has: the warning when the customer was invoiced, and its claims.
export function RoNotice({ roId }) {
  const { data: ro } = useLoad(() => dmsService.getRepairOrder(roId), [roId], { enabled: !!roId });
  if (!roId || !ro) return null;
  const billed = (ro.invoices || []).some((i) => i.ar_trx_id);
  const claims = ro.claims || [];
  return (
    <>
      {billed ? (
        <div role="alert" style={warnBox}>
          {ro.ro_number} already has a customer invoice. Claim only work the customer was not charged for. Work billed to both the customer and the manufacturer is counted twice.
        </div>
      ) : null}
      {claims.length ? (
        <div style={{ ...hint, marginBottom: 16 }}>
          Already claimed on this order: {claims.map((c) => `${c.claim_number} (${fmtMoney(c.claimed_amount)})`).join(", ")}
        </div>
      ) : null}
    </>
  );
}

const INITIAL_NEW = { claim_type: "SUPPLEMENTARY", lines: [blankLine()] };

export function NewClaimDrawer({ show, onHide, onDone }) {
  // Invoiced orders only: a claim cannot be raised before the order is invoiced.
  const { data: orders } = useLoad(
    async () => {
      const [a, b] = await Promise.all([dmsService.listRepairOrders({ status: "INVOICED", size: 100 }), dmsService.listRepairOrders({ status: "GATE_PASSED", size: 100 })]);
      return { success: true, data: [...(a?.data || []), ...(b?.data || [])].sort((x, y) => y.id - x.id) };
    },
    [show],
    { enabled: show }
  );
  const options = (orders || []).map((r) => ({ label: `${r.ro_number} · ${r.customer_name || "Walk-in"} · ${r.plate_no || r.vin || r.vehicle_desc || ""}`.replace(/ · $/, ""), value: r.id }));
  return (
    <FormDrawer
      show={show}
      title="New warranty claim"
      width={760}
      submitLabel="Create claim"
      initial={INITIAL_NEW}
      onHide={onHide}
      onDone={onDone}
      onSave={(clean) => dmsService.createWarrantyClaim({ ...clean, lines: claimLinesPayload(clean.lines) })}
      fields={[
        { key: "ro_id", label: "Repair order", type: "select", required: true, options, hint: "Only invoiced orders are listed" },
        {
          key: "claim_type",
          label: "Type of claim",
          type: "select",
          required: true,
          options: [
            { label: "Supplementary: work missed on the first claim", value: "SUPPLEMENTARY" },
            { label: "Goodwill: the manufacturer agreed to pay", value: "GOODWILL" },
          ],
        },
        { key: "reason", label: "Reason", type: "textarea", rows: 2, required: true, max: 500, hint: "Shown on the claim and kept in its history" },
        { key: "oem_reference", label: "Manufacturer's reference (if you have one)", max: 60 },
      ]}
    >
      {(values, setValues) => (
        <>
          <RoNotice roId={values.ro_id} />
          <ClaimLinesEditor lines={values.lines || [blankLine()]} onChange={(lines) => setValues((s) => ({ ...s, lines }))} />
        </>
      )}
    </FormDrawer>
  );
}

// The lines (and the reason, for a claim raised by hand) of a draft claim.
export function EditClaimDrawer({ claim, show, onHide, onDone }) {
  const manual = claim && claim.claim_type !== "REPAIR";
  const initial = claim
    ? {
        reason: claim.reason || "",
        lines: (claim.lines || []).map((l) => ({ line_kind: l.line_kind, description: l.description, quantity: Number(l.quantity), amount: Number(l.amount), job_id: l.job_id })),
      }
    : {};
  return (
    <FormDrawer
      show={show}
      title={claim ? `Edit ${claim.claim_number}` : "Edit claim"}
      width={760}
      initial={initial}
      onHide={onHide}
      onDone={onDone}
      onSave={(clean) => dmsService.warrantyClaimAction(claim.id, "lines", { lines: claimLinesPayload(clean.lines), ...(manual ? { reason: clean.reason } : {}) })}
      fields={manual ? [{ key: "reason", label: "Reason", type: "textarea", rows: 2, required: true, max: 500 }] : []}
    >
      {(values, setValues) => <ClaimLinesEditor lines={values.lines && values.lines.length ? values.lines : [blankLine()]} onChange={(lines) => setValues((s) => ({ ...s, lines }))} />}
    </FormDrawer>
  );
}

// Shown when there are no claims at all: where claims come from, and what has to be set up first.
export function NoClaimsHelp({ onNew }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "28px 24px", maxWidth: 720 }}>
      <div style={{ fontWeight: 600, color: "#374151", marginBottom: 6 }}>No warranty claims yet</div>
      <div style={{ fontSize: 14, color: "#4B5563", marginBottom: 8 }}>Claims are created for you. Nobody needs to enter them:</div>
      <ol style={{ fontSize: 14, color: "#4B5563", paddingLeft: 20, marginBottom: 12 }}>
        <li>On a repair order, add a job and set its pay type to <b>Warranty</b>.</li>
        <li>Finish the work and invoice the order.</li>
        <li>The warranty part of the order appears here as a <b>draft claim</b>. Check it, then submit it to the manufacturer.</li>
      </ol>
      <div style={{ ...hint, marginBottom: 12 }}>
        Set up once: each make needs the manufacturer's customer account number (Dealership Setup → Catalogue), so a submitted claim knows who to bill.
      </div>
      <div style={{ fontSize: 14, color: "#4B5563", marginBottom: 12 }}>To claim work on an order that is already invoiced (missed on the first claim, or agreed as goodwill), raise a claim by hand.</div>
      <SecondaryButton onClick={onNew}>New claim</SecondaryButton>
    </div>
  );
}
